import { useMemo, useState } from "react";
import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/toast-context";
import { CloverService, PendingCbuCloverBranch } from "../../services/clover-service";
import { isCBU, isBranchReadyForCloverCbu } from "../../utils/helpers";
import { CloverMerchantBadge } from "../branches/clover-merchant-badge";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "700px",
  maxWidth: 1200,
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "#fff",
  border: "1px solid #898989",
  boxShadow: 24,
  borderRadius: 4,
};

interface CloverCbuModalProps {
  isOpen: boolean;
  onClose: () => void;
  branches: PendingCbuCloverBranch[];
  onSaved: () => void;
}

export function CloverCbuModal({ isOpen, onClose, branches, onSaved }: CloverCbuModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const cloverService = useMemo(() => new CloverService(), []);
  const [cbuByBranch, setCbuByBranch] = useState<Record<number, string>>({});
  const [savingBranchId, setSavingBranchId] = useState<number | null>(null);

  const eligibleBranches = branches.filter((branch) =>
    isBranchReadyForCloverCbu(branch.status)
  );

  const handleCbuChange = (branchId: number, value: string) => {
    const digitsOnly = value.replace(/\D/g, "").slice(0, 22);
    setCbuByBranch((current) => ({ ...current, [branchId]: digitsOnly }));
  };

  const handleSave = async (branch: PendingCbuCloverBranch) => {
    const cbu = (cbuByBranch[branch.branchId] ?? "").trim();
    if (!isCBU(cbu)) {
      showToast(t("branches.cbu.invalid"), "error");
      return;
    }

    setSavingBranchId(branch.branchId);
    try {
      await cloverService.saveBranchCbu(
        branch.branchId,
        cbu,
        () => {
          showToast(t("branches.cbu.success"), "success");
          onSaved();
        },
        (errorMessage) => showToast(errorMessage, "error"),
        branch.identityNumber ?? undefined
      );
    } finally {
      setSavingBranchId(null);
    }
  };

  if (!isOpen || eligibleBranches.length === 0) return null;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="clover-cbu-modal"
      aria-describedby="Modal para cargar CBU de sucursales Clover"
    >
      <Box sx={style}>
        <div className="bg-gray-light text-center text-xl font-normal text-black py-8 px-2">
          <h1>{t("branches.cbu.title")}</h1>
        </div>
        <div className="p-6">
          <p className="text-gray-600 text-[15px] leading-7 mb-6">
            {t("branches.cbu.description")}
          </p>
          <div className="space-y-4">
            {eligibleBranches.map((branch) => {
              const cbuValue = cbuByBranch[branch.branchId] ?? "";
              const isSaving = savingBranchId === branch.branchId;
              return (
                <div
                  key={branch.branchId}
                  className="flex flex-col gap-3 p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Typography variant="h6">{branch.name}</Typography>
                        <CloverMerchantBadge
                          branchId={branch.branchId}
                          merchantId={branch.cloverExternalId}
                          branchName={branch.name}
                          variant="dark"
                          size={18}
                        />
                      </div>
                      {branch.address && (
                        <Typography variant="body2" color="textSecondary">
                          {branch.address}
                        </Typography>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-start">
                    <TextField
                      fullWidth
                      size="small"
                      label={t("branches.cbu.label")}
                      value={cbuValue}
                      onChange={(event) => handleCbuChange(branch.branchId, event.target.value)}
                      disabled={isSaving}
                      inputProps={{ inputMode: "numeric", maxLength: 22 }}
                      helperText={t("branches.cbu.helper")}
                      sx={{
                        "& .MuiInputBase-root": { fontFamily: "Montserrat" },
                        "& .MuiFormHelperText-root": { fontFamily: "Montserrat" },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={() => handleSave(branch)}
                      disabled={isSaving || !isCBU(cbuValue)}
                      sx={{
                        minWidth: "140px",
                        backgroundColor: "#EF0BB8",
                        textTransform: "none",
                        fontFamily: "Montserrat",
                        "&:hover": { backgroundColor: "#d009a6" },
                        "&:disabled": { backgroundColor: "#ccc", color: "#666" },
                      }}
                    >
                      {isSaving ? t("loading") : t("branches.cbu.save")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end mt-6">
            <Button
              onClick={onClose}
              sx={{
                color: "#898989",
                textTransform: "none",
                fontFamily: "Montserrat",
              }}
            >
              {t("branches.cbu.later")}
            </Button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}
